import { createClient } from '@supabase/supabase-js';
function mapUser(supabaseUser, profile) {
    return {
        id: supabaseUser.id,
        email: supabaseUser.email ?? '',
        fullName: profile?.fullName ?? null,
        avatarUrl: profile?.avatarUrl ?? null,
        tenantId: profile?.tenantId ?? '',
        role: profile?.role ?? 'member',
        clientId: profile?.clientId ?? null,
        isActive: true,
        isLensoraAdmin: profile?.isLensoraAdmin ?? false,
        createdAt: profile?.createdAt ? new Date(profile.createdAt) : new Date(),
    };
}
function mapSession(supabaseSession) {
    return {
        accessToken: supabaseSession.access_token,
        refreshToken: supabaseSession.refresh_token,
        expiresAt: supabaseSession.expires_at ?? 0,
        user: mapUser(supabaseSession.user),
    };
}
export class SupabaseAuthAdapter {
    client;
    constructor(config) {
        this.client = createClient(config.url, config.anonKey, 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        config.wsTransport ? { realtime: { transport: config.wsTransport } } : {});
    }
    async signInWithPassword(email, password) {
        const { data, error } = await this.client.auth.signInWithPassword({ email, password });
        if (error)
            throw new Error(error.message);
        if (!data.session)
            throw new Error('No session returned after sign in');
        return mapSession(data.session);
    }
    async signInWithOtp(email) {
        const { error } = await this.client.auth.signInWithOtp({ email });
        if (error)
            throw new Error(error.message);
    }
    async signOut() {
        const { error } = await this.client.auth.signOut();
        if (error)
            throw new Error(error.message);
    }
    async getSession() {
        const { data, error } = await this.client.auth.getSession();
        if (error)
            throw new Error(error.message);
        return data.session ? mapSession(data.session) : null;
    }
    async getUser() {
        const { data, error } = await this.client.auth.getUser();
        if (error)
            throw new Error(error.message);
        return data.user ? mapUser(data.user) : null;
    }
    async getUserByToken(token) {
        const { data, error } = await this.client.auth.getUser(token);
        if (error)
            throw new Error(error.message);
        return data.user ? mapUser(data.user) : null;
    }
    async refreshSession() {
        const { data, error } = await this.client.auth.refreshSession();
        if (error)
            throw new Error(error.message);
        return data.session ? mapSession(data.session) : null;
    }
    async setSession(accessToken, refreshToken) {
        const { data, error } = await this.client.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        if (error)
            throw new Error(error.message);
        return data.session ? mapSession(data.session) : null;
    }
    onAuthStateChange(callback) {
        const { data } = this.client.auth.onAuthStateChange((event, session) => {
            callback(session ? mapSession(session) : null, event);
        });
        return () => data.subscription.unsubscribe();
    }
    async signInWithOAuth(provider, redirectTo) {
        const { error } = await this.client.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo,
                queryParams: { prompt: 'select_account', access_type: 'offline' },
            },
        });
        if (error)
            throw new Error(error.message);
    }
    async resetPasswordForEmail(email, redirectTo) {
        const { error } = await this.client.auth.resetPasswordForEmail(email, { redirectTo });
        if (error)
            throw new Error(error.message);
    }
    async updatePassword(password) {
        const { error } = await this.client.auth.updateUser({ password });
        if (error)
            throw new Error(error.message);
    }
}
//# sourceMappingURL=supabase.js.map